"use client";

import Link from "next/link";
import { Star } from "lucide-react";
import { useEffect, useId, useState, type FormEvent } from "react";
import { Button } from "@/components/UI/Button";
import { RecaptchaNotice } from "@/components/UI/RecaptchaNotice";
import { useAccount } from "@/hooks/useAccount";
import { useRecaptcha } from "@/hooks/useRecaptcha";

// Formulário "Avaliar produto" (avaliações nativas). Envia para
// POST /api/product-reviews; a avaliação só aparece depois da moderação.
// Só cliente logado que comprou o produto (2026-10-17): o servidor confere
// (GET /api/product-reviews?productId=) e de novo no envio; o e-mail é o
// da conta.

type Eligibility = "loading" | "anonymous" | "not_purchased" | "already_reviewed" | "eligible" | "unavailable";

interface ProductReviewFormProps {
  productId: number;
  productName: string;
}

type FieldName = "rating" | "title" | "body" | "name" | "consent";
type SubmissionState = "idle" | "submitting" | "success" | "error";

const RATING_LABELS = ["", "Muito ruim", "Ruim", "Regular", "Bom", "Excelente"];
const inputClass =
  "w-full min-w-0 rounded-xl border border-slate-300 bg-white px-4 text-sm text-foreground outline-none placeholder:text-muted focus:border-primary focus:ring-2 focus:ring-primary/20";

export function ProductReviewForm({ productId, productName }: ProductReviewFormProps) {
  const baseId = useId();
  const account = useAccount();
  const [eligibility, setEligibility] = useState<Eligibility>("loading");
  const [accountName, setAccountName] = useState("");
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [state, setState] = useState<SubmissionState>("idle");
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({});
  const { getRecaptchaToken } = useRecaptcha();
  const id = (name: string) => `${baseId}-${name}`;

  // Reconfere ao entrar/sair da conta (a lateral de login não recarrega a página).
  useEffect(() => {
    if (account.status === "loading") return;
    let cancelled = false;
    fetch(`/api/product-reviews?productId=${productId}`, { cache: "no-store", credentials: "same-origin" })
      .then((response) => response.json())
      .then((data: { status?: string; name?: string }) => {
        if (cancelled) return;
        const known: Eligibility[] = ["anonymous", "not_purchased", "already_reviewed", "eligible"];
        setEligibility(known.includes(data.status as Eligibility) ? (data.status as Eligibility) : "unavailable");
        setAccountName(typeof data.name === "string" ? data.name : "");
      })
      .catch(() => {
        if (!cancelled) setEligibility("unavailable");
      });
    return () => {
      cancelled = true;
    };
  }, [account.status, productId]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (state === "submitting") return;
    const form = event.currentTarget;
    const data = new FormData(form);
    const payload = {
      productId,
      rating,
      title: String(data.get("title") ?? "").trim() || undefined,
      body: String(data.get("body") ?? ""),
      name: String(data.get("name") ?? ""),
      consent: data.get("consent") === "on",
      website: String(data.get("website") ?? ""),
    };
    const localErrors: Partial<Record<FieldName, string>> = {};
    if (rating < 1) localErrors.rating = "Escolha de 1 a 5 estrelas.";
    if (payload.body.trim().length < 10) localErrors.body = "Conte um pouco mais (mínimo de 10 caracteres).";
    if (payload.name.trim().length < 2) localErrors.name = "Informe seu nome.";
    if (!payload.consent) localErrors.consent = "Aceite a Política de Privacidade para enviar.";
    setErrors(localErrors);
    if (Object.keys(localErrors).length > 0) {
      setState("error");
      setMessage("Revise os campos destacados.");
      return;
    }

    setState("submitting");
    setMessage("");
    try {
      const recaptchaToken = (await getRecaptchaToken("product_review_submit")) ?? "";
      const response = await fetch("/api/product-reviews", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...payload, recaptchaToken }),
      });
      const result = (await response.json().catch(() => ({}))) as { message?: string; fieldErrors?: Partial<Record<FieldName, string>> };
      if (!response.ok) {
        setErrors(result.fieldErrors ?? {});
        setState("error");
        setMessage(result.message || "Não foi possível enviar a avaliação. Tente novamente.");
        return;
      }
      form.reset();
      setRating(0);
      setErrors({});
      setState("success");
      setMessage(result.message || "Obrigado! Sua avaliação será publicada depois da moderação.");
      setOpen(false);
      setEligibility("already_reviewed");
    } catch {
      setState("error");
      setMessage("Não foi possível enviar a avaliação. Tente novamente.");
    }
  }

  if (!open || eligibility !== "eligible") {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <p className="font-semibold text-heading">Já usou este produto?</p>
        <p className="mt-1 text-sm text-muted">Sua opinião ajuda outros clientes a escolher.</p>
        {state === "success" ? (
          <p className="mt-3 text-sm text-emerald-700" role="status">{message}</p>
        ) : eligibility === "loading" ? (
          <p className="mt-4 text-sm text-muted" role="status">Carregando…</p>
        ) : eligibility === "anonymous" ? (
          <>
            <p className="mt-3 text-sm text-foreground">Só clientes que compraram este produto podem avaliar. Entre na sua conta para continuar.</p>
            <Button onClick={() => window.dispatchEvent(new Event("persi:open-account-drawer"))} className="mt-4 w-full">
              Entrar para avaliar
            </Button>
          </>
        ) : eligibility === "not_purchased" ? (
          <p className="mt-3 rounded-xl bg-slate-50 px-3 py-2 text-sm text-foreground" role="status">
            A avaliação fica liberada depois que você comprar este produto com a sua conta.
          </p>
        ) : eligibility === "already_reviewed" ? (
          <p className="mt-3 rounded-xl bg-emerald-50 px-3 py-2 text-sm text-emerald-800" role="status">
            Você já avaliou este produto. Obrigado!
          </p>
        ) : eligibility === "unavailable" ? (
          <p className="mt-3 text-sm text-muted" role="status">As avaliações estão indisponíveis agora. Tente mais tarde.</p>
        ) : (
          <Button onClick={() => setOpen(true)} className="mt-4 w-full">
            Escrever avaliação
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <h3 className="font-semibold text-heading">Avaliar {productName}</h3>
      <form className="mt-4 space-y-4" noValidate onSubmit={handleSubmit}>
        <div className="sr-only" aria-hidden="true">
          <label htmlFor={id("website")}>Website</label>
          <input id={id("website")} name="website" type="text" tabIndex={-1} autoComplete="off" />
        </div>

        <fieldset aria-describedby={errors.rating ? id("rating-error") : undefined}>
          <legend className="mb-1.5 block text-sm font-medium text-foreground">Sua nota *</legend>
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((value) => (
              <label key={value} className="cursor-pointer rounded-lg p-1 focus-within:ring-2 focus-within:ring-primary">
                <input
                  type="radio"
                  name="rating"
                  value={value}
                  checked={rating === value}
                  onChange={() => setRating(value)}
                  className="sr-only"
                />
                <Star className={`h-7 w-7 ${value <= rating ? "fill-amber-400 text-amber-400" : "fill-slate-100 text-slate-300"}`} aria-hidden="true" />
                <span className="sr-only">{value} {value === 1 ? "estrela" : "estrelas"} ({RATING_LABELS[value]})</span>
              </label>
            ))}
            {rating > 0 ? <span className="ml-2 text-sm text-muted">{RATING_LABELS[rating]}</span> : null}
          </div>
          {errors.rating ? <p id={id("rating-error")} className="mt-1 text-sm text-red-700">{errors.rating}</p> : null}
        </fieldset>

        <div>
          <label htmlFor={id("title")} className="mb-1.5 block text-sm font-medium text-foreground">Título (opcional)</label>
          <input id={id("title")} name="title" type="text" maxLength={120} className={`${inputClass} h-11`} aria-invalid={Boolean(errors.title)} />
          {errors.title ? <p className="mt-1 text-sm text-red-700">{errors.title}</p> : null}
        </div>

        <div>
          <label htmlFor={id("body")} className="mb-1.5 block text-sm font-medium text-foreground">Sua avaliação *</label>
          <textarea
            id={id("body")}
            name="body"
            rows={5}
            maxLength={4000}
            className={`${inputClass} py-3`}
            aria-invalid={Boolean(errors.body)}
            aria-describedby={id("body-hint")}
          />
          <p id={id("body-hint")} className={`mt-1 text-xs ${errors.body ? "text-red-700" : "text-muted"}`}>
            {errors.body ?? "Conte como foi usar o produto. Não inclua links, e-mail ou telefone."}
          </p>
        </div>

        <div>
          <label htmlFor={id("name")} className="mb-1.5 block text-sm font-medium text-foreground">Nome *</label>
          <input id={id("name")} name="name" type="text" autoComplete="name" maxLength={80} defaultValue={accountName} className={`${inputClass} h-11`} aria-invalid={Boolean(errors.name)} aria-describedby={id("name-hint")} />
          <p id={id("name-hint")} className={`mt-1 text-xs ${errors.name ? "text-red-700" : "text-muted"}`}>{errors.name ?? "Publicamos só o primeiro nome e a inicial. Avaliação de compra verificada."}</p>
        </div>

        <label className="flex cursor-pointer items-start gap-2 text-sm leading-5 text-muted">
          <input name="consent" type="checkbox" className="mt-0.5 h-4 w-4 shrink-0 accent-primary" />
          <span>
            Li e aceito a{" "}
            <Link href="/politica-de-privacidade-e-seguranca" className="font-medium text-heading underline hover:text-secondary">
              Política de Privacidade
            </Link>
            . *
          </span>
        </label>
        {errors.consent ? <p className="-mt-2 text-sm text-red-700">{errors.consent}</p> : null}

        {/* Mesmo componente e mesma altura nos dois botões (celular: um embaixo do outro). */}
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button type="submit" disabled={state === "submitting"} className="w-full sm:flex-1">
            {state === "submitting" ? "Enviando..." : "Enviar avaliação"}
          </Button>
          <Button variant="outline" onClick={() => setOpen(false)} className="w-full sm:w-auto">
            Cancelar
          </Button>
        </div>
        <p className={`text-sm ${state === "success" ? "text-emerald-700" : state === "error" ? "text-red-700" : "text-muted"}`} role="status" aria-live="polite">
          {message}
        </p>
        <RecaptchaNotice />
      </form>
    </div>
  );
}
